import React, { useCallback, useState } from 'react'

export const keyMap = {
  ARROW_DOWN: 'ArrowDown',
  ARROW_UP: 'ArrowUp',
  TAB: 'Tab',
} as const

/**
 * /**
 * A React hook for managing cycled focus of elements in a list based on keyboard
 * arrows (up and down) and tabs input.
 *
 * It takes a list of element ids and returns the currently focused element id and
 * keydown event handler that should be attached to the list container. It also returns setFocusId
 * function that can be used to set the focused element id programmatically, for example, when
 * an element is clicked.
 *
 * Minimal example:
 *
```tsx
import React from 'react'
import { useRoveFocus } from 'use-rove-focus'


const List = () => {
  const items = [...]
  const { focusedId, handleKeyDown, setFocusedId } = useRoveFocus(items.map((item) => item.id));

  return (
    <ul onKeyDown={handleKeyDown}>
      {items.map((item) => (
        <ListItem
          key={item.id}
          label={item.label}
          focus={focusedIndex === idx}
        />
      ))}
    </ul>
  );
};

const ListItem = (props) => {
  const ref = React.useRef<HTMLLIElement>(null);
  React.useEffect(() => {
    if (props.focus) {
        // The actual focus call
        ref.current?.focus();
    }
  }, [props.focus]);
  return (
    <li ref={ref} tabIndex={props.focus ? 0 : -1}>
        {props.label}
    </li>
  );
};
```
*/
function useRoveFocus(elements: string[]) {
  const [focusedId, setFocusedId] = useState<string | undefined>(undefined)

  const lastIndex = elements.length - 1

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent | KeyboardEvent) => {
      event.preventDefault()
      const currentIndex = focusedId ? elements.indexOf(focusedId) : -1

      if (event.key === keyMap.ARROW_DOWN) {
        if (currentIndex < lastIndex) {
          setFocusedId(elements[currentIndex + 1])
        } else {
          setFocusedId(elements[0])
        }
      } else if (event.key === keyMap.ARROW_UP) {
        if (currentIndex > 0) {
          setFocusedId(elements[currentIndex - 1])
        } else {
          setFocusedId(elements[lastIndex])
        }
      }
    },
    [focusedId, elements, lastIndex],
  )

  return { focusedId, handleKeyDown, setFocusedId }
}

export default useRoveFocus
